export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}


let installPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

function notifyInstallListeners() {
  installListeners.forEach((listener) => listener());
}

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  installPrompt = event as BeforeInstallPromptEvent;
  notifyInstallListeners();
});

window.addEventListener("appinstalled", () => {
  installPrompt = null;
  notifyInstallListeners();
});

export function subscribeToInstallPrompt(listener: () => void) {
  installListeners.add(listener);
  return () => {
    installListeners.delete(listener);
  };
}

export function hasInstallPrompt() {
  return Boolean(installPrompt);
}

export async function showInstallPrompt() {
  if (!installPrompt) return false;
  const currentPrompt = installPrompt;
  await currentPrompt.prompt();
  const choice = await currentPrompt.userChoice;
  if (choice.outcome === "accepted") {
    installPrompt = null;
    notifyInstallListeners();
    return true;
  }
  return false;
}

export function isStandalone() {
  const navigatorWithStandalone = navigator as Navigator & {
    standalone?: boolean;
  };
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    navigatorWithStandalone.standalone === true
  );
}

export function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function supportsWebPush() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function syncVisualViewportHeight() {
  let animationFrame = 0;
  let orientationTimer = 0;
  const viewport = window.visualViewport;

  const update = () => {
    animationFrame = 0;
    const height = viewport?.height || window.innerHeight;
    document.documentElement.style.setProperty(
      "--app-height",
      `${Math.round(height)}px`,
    );
  };

  const scheduleUpdate = () => {
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    animationFrame = window.requestAnimationFrame(update);
  };

  const updateAfterOrientation = () => {
    scheduleUpdate();
    window.clearTimeout(orientationTimer);
    orientationTimer = window.setTimeout(scheduleUpdate, 300);
  };

  update();
  viewport?.addEventListener("resize", scheduleUpdate);
  viewport?.addEventListener("scroll", scheduleUpdate);
  window.addEventListener("resize", scheduleUpdate);
  window.addEventListener("orientationchange", updateAfterOrientation);
  window.addEventListener("focusin", scheduleUpdate);
  window.addEventListener("focusout", scheduleUpdate);
}

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const decoded = window.atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(decoded.length));
  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }
  return bytes;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register("/sw.js", {
    scope: "/",
    updateViaCache: "none",
  });
}
